import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Activity, CheckCircle2, FileText, CalendarDays } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicFormPage() {
  const { formId } = useParams();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking');
  const [survey, setSurvey] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [showSummary, setShowSummary] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/surveys/public/${formId}${bookingId ? `?booking=${bookingId}` : ''}`)
      .then(res => {
        setSurvey(res.data.survey);
        setAppointment(res.data.appointment);
      })
      .catch(() => setSurvey(null))
      .finally(() => setLoading(false));
  }, [formId, bookingId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!survey) return;
    const requiredFields = (survey.fields || []).filter(f => f.required);
    for (const field of requiredFields) {
      if (!responses[field.label]) {
        alert(`Please fill in: ${field.label}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/surveys/public/${formId}/submit`, {
        responses,
        appointment_id: bookingId,
        patient_id: appointment?.patient_id
      });
      setSubmitted(true);
      setShowSummary(res.data.show_summary);
      setSubmission(res.data.submission);
    } catch (err) {
      alert('Failed to submit form. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Activity className="w-8 h-8 text-sky-600 animate-pulse" />
    </div>
  );

  if (!survey) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Card className="max-w-md w-full mx-4"><CardContent className="p-8 text-center">
        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Form Not Found</h2>
        <p className="text-slate-500">This form may have been deactivated or the link is invalid.</p>
      </CardContent></Card>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="max-w-lg w-full">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Form Submitted!</h2>
          <p className="text-slate-500 mb-6">Thank you for completing the pre-visit form. Your responses have been recorded.</p>
          {appointment && (
            <div className="bg-sky-50 p-4 rounded-lg border border-sky-100 mb-4 text-left">
              <p className="text-sm font-semibold text-sky-800 mb-2 flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> Your Appointment</p>
              <p className="text-sm"><strong>Doctor:</strong> {appointment.doctor_name}</p>
              {appointment.date && <p className="text-sm"><strong>Date:</strong> {appointment.date} {appointment.time && `at ${appointment.time}`}</p>}
            </div>
          )}
          {showSummary && submission && (
            <div className="bg-slate-50 p-4 rounded-lg border text-left">
              <p className="text-sm font-semibold text-slate-800 mb-2">Your Responses Summary:</p>
              {Object.entries(submission.responses || {}).map(([key, val]) => (
                <div key={key} className="text-sm py-1 border-b border-slate-100 last:border-0">
                  <span className="text-slate-500">{key}:</span> <span className="font-medium">{String(val)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <div className="w-10 h-10 rounded-xl bg-sky-600 flex items-center justify-center mx-auto mb-3">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-sm font-medium text-sky-600">engagingly.ai</h1>
        </div>

        {appointment && (
          <Card className="mb-4 border-sky-200 bg-sky-50">
            <CardContent className="p-4">
              <p className="text-sm text-sky-800">
                <strong>Appointment with {appointment.doctor_name}</strong>
                {appointment.date && <> on {appointment.date} {appointment.time && `at ${appointment.time}`}</>}
              </p>
            </CardContent>
          </Card>
        )}

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>{survey.name}</CardTitle>
            {survey.description && <CardDescription>{survey.description}</CardDescription>}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {(survey.fields || []).map((field, idx) => (
                <div key={field.id || idx} className="space-y-2" data-testid={`public-field-${idx}`}>
                  <Label className="text-sm font-medium">
                    {field.label || `Question ${idx + 1}`}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  {field.type === 'textarea' ? (
                    <Textarea value={responses[field.label] || ''} onChange={e => setResponses({ ...responses, [field.label]: e.target.value })} placeholder={field.placeholder} rows={3} required={field.required} />
                  ) : field.type === 'select' ? (
                    <Select value={responses[field.label] || ''} onValueChange={v => setResponses({ ...responses, [field.label]: v })}>
                      <SelectTrigger><SelectValue placeholder={field.placeholder || 'Select...'} /></SelectTrigger>
                      <SelectContent>{(field.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : field.type === 'radio' ? (
                    <div className="space-y-2">{(field.options || []).map(o => (
                      <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="radio" name={field.label} value={o} checked={responses[field.label] === o} onChange={() => setResponses({ ...responses, [field.label]: o })} className="accent-sky-600" />
                        {o}
                      </label>
                    ))}</div>
                  ) : field.type === 'checkbox' ? (
                    <div className="space-y-2">{(field.options || []).map(o => (
                      <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={(responses[field.label] || []).includes(o)} onChange={(e) => {
                          const current = responses[field.label] || [];
                          setResponses({ ...responses, [field.label]: e.target.checked ? [...current, o] : current.filter(x => x !== o) });
                        }} className="accent-sky-600" />
                        {o}
                      </label>
                    ))}</div>
                  ) : (
                    <Input type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'email' ? 'email' : 'text'}
                      value={responses[field.label] || ''} onChange={e => setResponses({ ...responses, [field.label]: e.target.value })}
                      placeholder={field.placeholder} required={field.required} />
                  )}
                </div>
              ))}
              <Button type="submit" className="w-full bg-sky-600 hover:bg-sky-700 h-11" disabled={submitting} data-testid="public-form-submit">
                {submitting ? 'Submitting...' : 'Submit Form'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
